'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';
import Link from 'next/link';
import { RefreshCw, Home, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Inter, Baumans } from 'next/font/google';
const inter = Inter({
  subsets: ['latin', 'cyrillic'],
  variable: '--font-sans',
  display: 'swap'
});
const baumans = Baumans({
  subsets: ['latin'],
  variable: '--font-baumans',
  weight: '400',
  display: 'swap',
  preload: true
});
interface GlobalErrorProps {
  error: Error & {
    digest?: string;
  };
  reset: () => void;
}
export default function GlobalError(t0) {
  const $ = _c(18);
  const {
    error,
    reset
  } = t0;
  let t1;
  let t2;
  if ($[0] !== error) {
    t1 = () => {
      Sentry.captureException(error);
      console.error("[GlobalErrorBoundary]", error);
    };
    t2 = [error];
    $[0] = error;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="pointer-events-none absolute inset-0"><div className="absolute -top-24 -right-20 size-56 rounded-full bg-primary/10 blur-3xl dark:bg-primary/15" /><div className="absolute -bottom-28 -left-20 size-72 rounded-full bg-secondary/10 blur-3xl dark:bg-secondary/20" /></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  let t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="inline-flex items-center justify-center rounded-full border bg-accent/30 dark:bg-accent/20 size-16 shadow-sm"><TriangleAlert className="size-8 text-destructive" /></div>;
    t5 = <h1 className="font-be-vietnam-pro text-3xl md:text-4xl font-semibold tracking-tight">Что-то сломалось</h1>;
    t6 = <p className="text-muted-foreground leading-relaxed">Произошла критическая ошибка приложения. Попробуйте восстановить страницу или вернитесь на главную.</p>;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <RefreshCw className="size-4" />;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== reset) {
    t8 = <Button onClick={reset} className="rounded-full">{t7}Попробовать снова</Button>;
    $[8] = reset;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Link href="/" prefetch={true}><Button variant="outline" className="rounded-full"><Home className="size-4" />На главную</Button></Link>;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== t8) {
    t10 = <div className="flex flex-wrap items-center justify-center gap-3 pt-2">{t8}{t9}</div>;
    $[11] = t8;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== error.digest) {
    t11 = error.digest && <p className="text-xs text-muted-foreground pt-4">Код ошибки: {error.digest}</p>;
    $[13] = error.digest;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t10 || $[16] !== t11) {
    t12 = <html lang="ru" suppressHydrationWarning={true}><body className={`${inter.variable} ${baumans.variable} font-sans antialiased bg-background text-foreground`} suppressHydrationWarning={true}><div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-background"><div className="w-full max-w-lg"><div className="relative rounded-2xl border bg-card/60 backdrop-blur-sm shadow-lg p-8 overflow-hidden">{t3}<div className="relative flex flex-col items-center text-center gap-5">{t4}{t5}{t6}{t10}{t11}</div></div></div></div></body></html>;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  return t12;
}